<template>
  <Teleport to="body">
    <div class="toast-container" aria-live="polite" aria-atomic="false">
      <TransitionGroup
        name="toast"
        tag="div"
        class="toast-stack"
      >
        <div
          v-for="t in toasts"
          :key="t.id"
          class="toast-item"
          :class="`toast--${t.variant}`"
          role="status"
        >
          <span class="toast-icon">
            <CheckCircle2 v-if="t.variant === 'success'" class="h-5 w-5" />
            <XCircle v-else-if="t.variant === 'error'" class="h-5 w-5" />
            <AlertTriangle v-else-if="t.variant === 'warning'" class="h-5 w-5" />
            <Info v-else class="h-5 w-5" />
          </span>

          <p class="toast-message">{{ t.message }}</p>

          <button class="toast-close" @click="dismiss(t.id)" aria-label="Dismiss">
            <X class="h-4 w-4" />
          </button>

          <div class="toast-progress">
            <div
              class="toast-progress-bar"
              :class="`toast-progress-bar--${t.variant}`"
              :style="{ animationDuration: `${t.duration}ms` }"
            />
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useToast } from '@/composables/ui/useToast'
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-vue-next'

const { toasts, dismiss } = useToast()
</script>

<style scoped>
/* ============================================
   CONTAINER
   ============================================ */

.toast-container {
  @apply fixed bottom-6 right-6 z-[100]
         pointer-events-none;
}

.toast-stack {
  @apply flex flex-col-reverse gap-3;
}

/* ============================================
   INDIVIDUAL TOAST
   ============================================ */

.toast-item {
  @apply relative
         flex items-start gap-3
         w-80 max-w-[calc(100vw-3rem)]
         px-4 py-3.5
         rounded-2xl
         border
         shadow-lg
         backdrop-blur-md
         pointer-events-auto
         overflow-hidden;
}

/* Variants */
.toast--success {
  @apply bg-white/95 dark:bg-gray-800/95
         border-success/30 dark:border-success-dark/30;
}

.toast--error {
  @apply bg-white/95 dark:bg-gray-800/95
         border-danger/30 dark:border-danger-dark/30;
}

.toast--warning {
  @apply bg-white/95 dark:bg-gray-800/95
         border-warning/30 dark:border-warning-dark/30;
}

.toast--info {
  @apply bg-white/95 dark:bg-gray-800/95
         border-info/30 dark:border-info-dark/30;
}

/* ============================================
   ICON
   ============================================ */

.toast-icon {
  @apply flex-shrink-0 mt-0.5;
}

.toast--success .toast-icon { @apply text-success dark:text-success-dark; }
.toast--error   .toast-icon { @apply text-danger dark:text-danger-dark; }
.toast--warning .toast-icon { @apply text-warning dark:text-warning-dark; }
.toast--info    .toast-icon { @apply text-info dark:text-info-dark; }

/* ============================================
   MESSAGE
   ============================================ */

.toast-message {
  @apply flex-1 text-sm leading-snug
         text-gray-800 dark:text-gray-200;
}

/* ============================================
   CLOSE
   ============================================ */

.toast-close {
  @apply flex-shrink-0 -mt-0.5 -mr-1 p-1
         rounded-lg
         text-gray-400 dark:text-gray-500
         hover:text-gray-600 dark:hover:text-gray-300
         hover:bg-gray-100 dark:hover:bg-gray-700
         transition-colors duration-100;
}

/* ============================================
   PROGRESS BAR
   ============================================ */

.toast-progress {
  @apply absolute bottom-0 left-0 right-0 h-1
         bg-gray-100 dark:bg-gray-700;
}

.toast-progress-bar {
  @apply h-full rounded-r-full;
  animation: toast-shrink linear forwards;
}

.toast-progress-bar--success { @apply bg-success dark:bg-success-dark; }
.toast-progress-bar--error   { @apply bg-danger dark:bg-danger-dark; }
.toast-progress-bar--warning { @apply bg-warning dark:bg-warning-dark; }
.toast-progress-bar--info    { @apply bg-info dark:bg-info-dark; }

@keyframes toast-shrink {
  from { width: 100%; }
  to   { width: 0%; }
}

/* ============================================
   TRANSITIONS
   ============================================ */

.toast-enter-active {
  transition: all 0.35s cubic-bezier(0.21, 1.02, 0.73, 1);
}

.toast-leave-active {
  transition: all 0.25s cubic-bezier(0.06, 0.71, 0.55, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.9);
}
</style>
